import useSWR from 'swr';

import type { AccountType } from '@pfo/domain';

import {
  fetchAccountStatement,
  fetchAccountsOverview,
  fetchOpeningEntry,
  type AccountsOverview,
  type OpeningEntry,
  type StatementMonth,
} from '../data/accountsRepository.js';

/** One key per read, exported so a mutation can revalidate exactly what it changed. */
export const ACCOUNTS_KEY = 'accounts/overview';
export const accountStatementKey = (accountId: string) => `accounts/statement/${accountId}`;
export const openingEntryKey = (accountId: string) => `accounts/opening/${accountId}`;

export type AccountsState =
  | { status: 'loading' }
  | { status: 'error'; message: string; retry: () => void }
  | { status: 'ready'; overview: AccountsOverview; refresh: () => Promise<unknown> };

export function useAccounts(): AccountsState {
  const { data, error, isLoading, mutate } = useSWR<AccountsOverview, Error>(
    ACCOUNTS_KEY,
    fetchAccountsOverview,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  if (error) {
    return {
      status: 'error',
      message: error.message || 'Không tải được danh sách tài khoản.',
      retry: () => {
        void mutate();
      },
    };
  }

  if (isLoading || !data) return { status: 'loading' };

  return { status: 'ready', overview: data, refresh: () => mutate() };
}

export type StatementState =
  | { status: 'loading' }
  | { status: 'error'; message: string; retry: () => void }
  | { status: 'ready'; months: readonly StatementMonth[] };

export function useAccountStatement(accountId: string): StatementState {
  const { data, error, isLoading, mutate } = useSWR<readonly StatementMonth[], Error>(
    accountStatementKey(accountId),
    () => fetchAccountStatement(accountId),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );

  if (error) {
    return {
      status: 'error',
      message: error.message || 'Không tải được sao kê.',
      retry: () => {
        void mutate();
      },
    };
  }

  if (isLoading || !data) return { status: 'loading' };

  return { status: 'ready', months: data };
}

export type OpeningEntryState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; opening: OpeningEntry | null };

/** The account's opening entry, so the detail page can offer to correct it (AJ1). */
export function useOpeningEntry(accountId: string, accountType: AccountType): OpeningEntryState {
  const { data, error, isLoading } = useSWR<OpeningEntry | null, Error>(
    openingEntryKey(accountId),
    () => fetchOpeningEntry(accountId, accountType),
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  if (error) return { status: 'error', message: error.message || 'Không đọc được số dư đầu kỳ.' };
  if (isLoading || data === undefined) return { status: 'loading' };
  return { status: 'ready', opening: data };
}
